import { useState, useCallback } from 'react';
import axios from 'axios';
import { ChatMessage, ChatResponse, ChatError, SendMessageRequest } from '../types/chat.types';
import { FirebaseUserProps } from '../../../common/entityUtilities';
import { setAxiosDefaultHeaders } from '../../../common/apiBaseUtilities';

// Using existing REACT_APP_CPAP_API environment variable
const API_BASE_URL = process.env.REACT_APP_CPAP_API || 'http://localhost:3001';

export const useChatApi = (firebaseUser: FirebaseUserProps | undefined) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);

  const sendMessage = useCallback(async (
    request: SendMessageRequest
  ): Promise<ChatMessage | null> => {
    if (!firebaseUser) {
      setError({
        code: 'AUTH_ERROR',
        message: 'You must be logged in to use the chatbot.',
      });
      return null;
    }
    setLoading(true);
    setError(null);

    const { uid, bearerToken } = firebaseUser;
    setAxiosDefaultHeaders(uid, bearerToken);

    try {
      const response = await axios.post<ChatResponse>(
        `${API_BASE_URL}chat`,
        request,
        {
          timeout: 30000, // 30 second timeout
        }
      );

      const assistantMessage: ChatMessage = {
        id: response.data.id,
        role: 'assistant',
        message: response.data.message,
        timestamp: response.data.timestamp,
        sources: response.data.sources,
        metadata: response.data.metadata,
      };

      setLoading(false);
      return assistantMessage;
    } catch (err: any) {
      const chatError: ChatError = {
        code: err.response?.data?.error?.code || 'NETWORK_ERROR',
        message: err.response?.data?.error?.message || 'Failed to send message. Please try again.',
        details: err.response?.data?.error?.details,
        retryAfter: err.response?.data?.error?.retryAfter,
      };

      setError(chatError);
      setLoading(false);
      return null;
    }
  }, [firebaseUser]);

  const getChatHistory = useCallback(async (sessionId: string): Promise<ChatMessage[]> => {
    if (!firebaseUser) return [];

    const { uid, bearerToken } = firebaseUser;
    setAxiosDefaultHeaders(uid, bearerToken);

    try {
      const response = await axios.get(`${API_BASE_URL}chat/history/${sessionId}`);
      return response.data.messages || [];
    } catch (err) {
      console.error('Failed to fetch chat history:', err);
      return [];
    }
  }, [firebaseUser]);

  const sendFeedback = useCallback(async (
    messageId: string,
    sessionId: string,
    feedback: 'positive' | 'negative',
    comment?: string
  ): Promise<boolean> => {
    if (!firebaseUser) return false;

    const { uid, bearerToken } = firebaseUser;
    setAxiosDefaultHeaders(uid, bearerToken);

    try {
      await axios.post(`${API_BASE_URL}chat/feedback`, {
        messageId,
        sessionId,
        feedback,
        comment,
      });
      return true;
    } catch (err) {
      console.error('Failed to send feedback:', err);
      return false;
    }
  }, [firebaseUser]);

  return {
    sendMessage,
    getChatHistory,
    sendFeedback,
    loading,
    error,
  };
};
